/**
 * sl-m82f.4.1: React hook that loads the feature-totals summary from the
 * backend and normalizes bigint-as-string fields into display credits.
 *
 * Used by `UsageAnalytics` to drive the top-level tab summary. Tab
 * components themselves receive the normalized `FeatureTotals` via props
 * so they don't each re-trigger the request.
 */
import { useCallback, useEffect, useState } from 'react';

import { fetchFeatureTotals } from './analytics/api';
import { microStringToCredits } from './analytics/format';
import type {
  AnalyticsPeriodRange,
  FeatureTotals,
  FeatureTotalsKey,
  FeatureTotalsResponse,
} from './analytics/types';

export interface UseFeatureTotalsResult {
  data: FeatureTotals | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const FEATURE_KEYS: readonly FeatureTotalsKey[] = [
  'aiChat',
  'intelligentSearch',
  'aiMemory',
  'businessIntelligence',
  'imageGen',
  'transcription',
  'websearch',
  'workflows',
] as const;

function normalize(response: FeatureTotalsResponse): FeatureTotals {
  const features = {} as Record<FeatureTotalsKey, number>;
  for (const key of FEATURE_KEYS) {
    features[key] = microStringToCredits(response.features[key] ?? '0');
  }
  return {
    periodStart: response.periodStart,
    periodEnd: response.periodEnd,
    totals: {
      aiCredits: microStringToCredits(response.totals.aiUsedMicro),
      workflowCredits: microStringToCredits(response.totals.workflowUsedMicro),
      otherCredits: microStringToCredits(response.totals.otherUsedMicro),
      totalCredits: microStringToCredits(response.totals.totalUsedMicro),
    },
    features,
  };
}

/**
 * Fetch `/feature-totals` for the given period. `period` is passed through
 * to the endpoint — pass `"current"` to use the tenant's active billing
 * period, or a YYYY-MM string for historical drill-down.
 */
export const useFeatureTotals = (
  workspaceId: string,
  period: string | AnalyticsPeriodRange
): UseFeatureTotalsResult => {
  const [data, setData] = useState<FeatureTotals | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchFeatureTotals(workspaceId, period);
      setData(normalize(response));
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : 'Failed to load totals';
      setError(message);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, period]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, isLoading, error, refetch: load };
};
